import {useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {sha256} from 'js-sha256';
import {Alert, Box, Tab, Tabs, Typography} from '@mui/material';
import {setUserToken} from '../../../utils/api';
import Dashboard1 from './Dashboard1';
import Dashboard2 from './Dashboard2';
import Dashboard3 from './Dashboard3';
import Dashboard4 from './Dashboard4';
import Legend from '../components/Legend';

const TABS = [
    {label: 'Dashboard 1', subtitle: 'Intervento di rigenerazione'},
    {label: 'Dashboard 2', subtitle: 'Iniziative realizzate'},
    {label: 'Dashboard 3', subtitle: 'Frequenza e interesse dei fruitori'},
    {label: 'Dashboard 4', subtitle: 'Attività partecipative svolte'},
];

interface Props {
    edition: '2023' | '2024';
}

const DashboardIndex = ({edition}: Props) => {
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(0);

    const container = searchParams.get('container_id');
    const uoaId = searchParams.get('uoa_id');
    const survey0Id = searchParams.get('survey0_id');
    const surveyAggiornamentoLuoghiId = searchParams.get('survey_aggiornamento_luoghi_id');
    const survey5Id = searchParams.get('survey5_id');
    const survey1Id = searchParams.get('survey1_id');
    const survey2Id = searchParams.get('survey2_id');
    const survey3Id = searchParams.get('survey3_id');
    const survey4Id = searchParams.get('survey4_id');
    const hashKey = searchParams.get('key');
    const token = searchParams.get('token');

    const hashKeyLocal = sha256(process.env.REACT_APP_HASH_KEY_FSV || '');

    if (token) {
        setUserToken(token);
    }

    const isAuthorized = !!(
        container &&
        uoaId &&
        survey0Id &&
        surveyAggiornamentoLuoghiId &&
        survey5Id &&
        survey1Id &&
        survey2Id &&
        survey3Id &&
        survey4Id &&
        hashKey === hashKeyLocal
    );

    if (!isAuthorized) {
        return (
            <Alert severity='error'>
                Parametri mancanti o chiave non valida.
            </Alert>
        );
    }

    const technicalComplexityTitle = "Complessità tecnica prevista dell'intervento a confronto con la media";
    const ageGroupQuestion = edition === '2024' ? 'domanda2' : 'question9';

    return (
        <Box>
            <Box sx={{borderBottom: 1, borderColor: 'divider', mb: 3}}>
                <Tabs
                    value={activeTab}
                    onChange={(_, value) => setActiveTab(value)}
                    variant='scrollable'
                    scrollButtons='auto'
                >
                    {TABS.map((tab, i) => (
                        <Tab
                            key={i}
                            label={
                                <Box sx={{textAlign: 'left'}}>
                                    <Typography variant='body2' fontWeight={500}>
                                        {tab.label}
                                    </Typography>
                                    <Typography variant='caption' color='text.disabled'>
                                        {tab.subtitle}
                                    </Typography>
                                </Box>
                            }
                        />
                    ))}
                </Tabs>
            </Box>

            <Legend />

            <Box role='tabpanel' hidden={activeTab !== 0}>
                {activeTab === 0 && (
                    <Dashboard1
                        container={container!}
                        uoaId={uoaId!}
                        surveyId={survey0Id!}
                        aggiornamentoSurveyId={surveyAggiornamentoLuoghiId!}
                        technicalComplexityTitle={technicalComplexityTitle}
                    />
                )}
            </Box>

            <Box role='tabpanel' hidden={activeTab !== 1}>
                {activeTab === 1 && (
                    <Dashboard2
                        container={container!}
                        uoaId={uoaId!}
                        inizialeSurveyId={survey1Id!}
                        aggiornamentoSurveyId={survey2Id!}
                        finaleSurveyId={survey3Id!}
                        ageGroupQuestion={ageGroupQuestion}
                    />
                )}
            </Box>

            <Box role='tabpanel' hidden={activeTab !== 2}>
                {activeTab === 2 && (
                    <Dashboard3
                        container={container!}
                        uoaId={uoaId!}
                        surveyId={survey4Id!}
                    />
                )}
            </Box>

            <Box role='tabpanel' hidden={activeTab !== 3}>
                {activeTab === 3 && (
                    <Dashboard4
                        container={container!}
                        uoaId={uoaId!}
                        surveyId={survey5Id!}
                    />
                )}
            </Box>
        </Box>
    );
};

export default DashboardIndex;
